import { Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';

import useFetchDeductibleCharges from 'src/Hooks/Queries/useFetchDeductibleCharges';

import styles from './DeductibleCharges.module.scss';
import { Props } from './DeductibleCharges.types';

const DeductibleCharges = ({ year }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchDeductibleCharges(year);

  return (
    <div className={styles.container}>
      <div className={styles.statsGrid}>
        <div className={styles.statTile}>
          <div className={styles.statLabel}>
            {t('expenses.deductibleCharges.totalLocative', 'Total des charges locatives')}
          </div>
          <div className={styles.statAmount}>{(data?.totalLocativeAmount ?? 0).toFixed(2)} €</div>
        </div>
        <div className={styles.statTile}>
          <div className={styles.statLabel}>
            {t('expenses.deductibleCharges.userQuotePart', 'Votre quote-part déductible')}
          </div>
          <div className={styles.statAmount}>{(data?.userQuotePart ?? 0).toFixed(2)} €</div>
        </div>
      </div>

      <DataTable
        withRowBorders={false}
        style={{ width: '100%', minHeight: '10rem' }}
        records={data?.expenses ?? []}
        idAccessor="expenseId"
        fetching={isLoading}
        noRecordsText={t(
          'expenses.deductibleCharges.empty',
          'Aucune dépense locative pour cette année'
        )}
        columns={[
          {
            accessor: 'title',
            title: t('expenses.deductibleCharges.column.title', 'Dépense'),
          },
          {
            accessor: 'paidAt',
            title: t('expenses.deductibleCharges.column.date', 'Date'),
            render: ({ paidAt }) => paidAt.format('L'),
          },
          {
            accessor: 'amount',
            title: t('expenses.deductibleCharges.column.amount', 'Montant'),
            render: ({ amount }) => `${amount.toFixed(2)} €`,
          },
          {
            accessor: 'locativeShare',
            title: t('expenses.deductibleCharges.column.share', 'Part locative'),
            render: ({ locativeShare }) => `${locativeShare}%`,
          },
          {
            accessor: 'locativeAmount',
            title: t('expenses.deductibleCharges.column.locativeAmount', 'Montant locatif'),
            render: ({ locativeAmount }) => `${locativeAmount.toFixed(2)} €`,
          },
          {
            accessor: 'userQuotePart',
            title: t('expenses.deductibleCharges.column.userQuotePart', 'Votre quote-part'),
            render: ({ userQuotePart }) => `${userQuotePart.toFixed(2)} €`,
          },
        ]}
      />

      <Alert
        className={styles.disclaimer}
        variant="light"
        color="gray"
        icon={<IconInfoCircle />}
        title={t('expenses.deductibleCharges.disclaimerTitle', 'Avertissement fiscal')}
      >
        {t(
          'expenses.deductibleCharges.disclaimerContent',
          "Ces montants sont fournis à titre indicatif pour vous aider à préparer votre déclaration (formulaire 2044). Ils ne remplacent pas l'avis d'un expert-comptable ou d'un conseiller fiscal."
        )}
      </Alert>
    </div>
  );
};

export default DeductibleCharges;
